import React, { useEffect, useMemo, useState } from 'react';
import storageManager from '../../utils/storageManager';
import { API_BASE_URL } from '../../config';
import CandidateCard from './CandidateCard';
import ContactHistorySection from './ContactHistorySection';
import CVExpandedSection from './CVExpandedSection';
import MetricCards from './MetricCards';
import RemoveReasonModal from './RemoveReasonModal';
import ResultsHeader from './ResultsHeader';

/**
 * Modern Query Results List Component
 * Combines QueryResultsList.js functionality with modern Metronic design
 * Based on conversation-mockup.html
 */
export function ModernQueryResultsList({
  results: initialResults,
  onCandidateRemove,
  removedCandidates
}) {
  const [results, setResults] = useState([]);
  const [expandedResults, setExpandedResults] = useState(new Set());
  const [removedCount, setRemovedCount] = useState(0);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [selectedCandidates, setSelectedCandidates] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [removalPatterns, setRemovalPatterns] = useState({
    reasons: {},
    skills: {},
    thresholds: {
      matchScore: null,
      similarity: null
    }
  });

  // Remove reason modal state
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [candidateToRemove, setCandidateToRemove] = useState(null);

  // Initialize job ID
  useEffect(() => {
    const storedJobId = localStorage.getItem('currentJobId');
    setCurrentJobId(storedJobId);

    console.log('ModernQueryResultsList initialization:', {
      storedJobId,
      hasResults: Boolean(initialResults),
      resultsCount: initialResults?.length
    });
  }, [initialResults]);

  // Process and filter results
  useEffect(() => {
    console.log('ModernQueryResultsList received:', {
      total: initialResults?.length,
      removedCount: removedCandidates?.size,
      jobId: currentJobId
    });

    if (initialResults && Array.isArray(initialResults)) {
      const filteredResults = initialResults.filter(result => !removedCandidates?.has(result.id));

      // Add jobId to each result and preserve contact history
      const resultsWithJobId = filteredResults.map(result => {
        // Log contact history for debugging
        if (result.contactHistory) {
          console.log(`ModernQueryResultsList - Candidate ${result.id} has contact history:`,
            JSON.stringify(result.contactHistory));
        }

        return {
          ...result,
          jobId: currentJobId,
          // Ensure contact history is preserved
          contactHistory: result.contactHistory || null
        };
      });

      setResults(resultsWithJobId);
      setRemovedCount(initialResults.length - filteredResults.length);

      if (currentJobId) {
        storageManager.storeResults(currentJobId, initialResults);
        storageManager.storeFilteredResults(currentJobId, resultsWithJobId);
        console.log('Stored results via storageManager for job:', currentJobId);
      }

      console.log('Filtered results:', {
        before: initialResults.length,
        after: filteredResults.length,
        removed: initialResults.length - filteredResults.length,
        jobId: currentJobId
      });
    }
  }, [initialResults, removedCandidates, currentJobId]);

  // Handle candidate removal
  const handleRemoveCandidate = async (candidateId, removalData) => {
    try {
      console.log('Removing candidate:', {
        id: candidateId,
        currentRemoved: removedCount
      });

      // Notify parent component
      if (onCandidateRemove) {
        onCandidateRemove(candidateId);
      }

      // Update local state
      setResults(prev => prev.filter(result => result.id !== candidateId));
      setRemovedCount(prev => prev + 1);

      // Analyze removal patterns
      analyzeRemovals(removalData);

    } catch (error) {
      console.error('Error handling candidate removal:', error);
    }
  };

  // Show remove modal
  const handleShowRemoveModal = (candidateId) => {
    const candidate = results.find(r => r.id === candidateId);
    setCandidateToRemove(candidate);
    setShowRemoveModal(true);
  };

  // Confirm removal from modal
  const handleConfirmRemoval = (reason) => {
    if (!candidateToRemove) return;

    const getDisplayName = (result) => {
      if (result.name && result.name.trim()) return result.name;
      if (result.id) return result.id.split('/').pop().replace('.pdf', '');
      return "Unnamed Candidate";
    };

    const removalData = {
      candidateId: candidateToRemove.id,
      candidateName: getDisplayName(candidateToRemove),
      matchScore: candidateToRemove.similarity,
      keywordMatches: candidateToRemove.keyword_match_count || 0,
      combinedScore: candidateToRemove.combined_score || 0,
      removalReason: reason,
      timestamp: new Date().toISOString(),
      jobTitle: candidateToRemove.title,
      skills: candidateToRemove.matchingContext?.key_matches || []
    };

    handleRemoveCandidate(candidateToRemove.id, removalData);
    setShowRemoveModal(false);
    setCandidateToRemove(null);
  };

  // Toggle CV expansion
  const toggleExpand = (id) => {
    setExpandedResults((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Analyze removal patterns
  const analyzeRemovals = (removalData) => {
    try {
      setRemovalPatterns(prev => {
        const newPatterns = { ...prev };

        // Track removal reasons
        newPatterns.reasons[removalData.removalReason] = (newPatterns.reasons[removalData.removalReason] || 0) + 1;

        // Track removed skills
        if (removalData.skills && removalData.skills.length > 0) {
          removalData.skills.forEach(skill => {
            newPatterns.skills[skill] = (newPatterns.skills[skill] || 0) + 1;
          });
        }

        // Track score thresholds
        if (removalData.matchScore) {
          const scores = Object.values(newPatterns.thresholds.matchScore || {});
          newPatterns.thresholds.matchScore = scores.length > 0
            ? (Math.max(...scores) + removalData.matchScore) / 2
            : removalData.matchScore;
        }

        // Store patterns in localStorage for persistence
        localStorage.setItem('removalPatterns', JSON.stringify(newPatterns));

        console.log('Updated removal patterns:', newPatterns);
        return newPatterns;
      });

      // Send patterns to backend for learning
      fetch(`${API_BASE_URL}/api/candidates/removals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(removalData)
      }).catch(error => console.error('Error sending removal patterns:', error));

    } catch (error) {
      console.error('Error analyzing removals:', error);
    }
  };

  // Load saved patterns on mount
  useEffect(() => {
    const savedPatterns = localStorage.getItem('removalPatterns');
    if (savedPatterns) {
      setRemovalPatterns(JSON.parse(savedPatterns));
    }
  }, []);

  // Selection handlers
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedCandidates(new Set(results.map(result => result.id)));
    } else {
      setSelectedCandidates(new Set());
    }
    setSelectAll(checked);
  };

  const handleSelectCandidate = (candidateId) => {
    setSelectedCandidates(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(candidateId)) {
        newSelected.delete(candidateId);
      } else {
        newSelected.add(candidateId);
      }
      // Update selectAll state
      setSelectAll(newSelected.size === results.length && results.length > 0);
      return newSelected;
    });
  };

  const handleBulkRemove = async () => {
    if (selectedCandidates.size === 0) return;

    const reason = prompt('Please enter a reason for removing these candidates:');
    if (!reason) return;

    for (const candidateId of selectedCandidates) {
      const candidate = results.find(r => r.id === candidateId);
      if (candidate) {
        const getDisplayName = (result) => {
          if (result.name && result.name.trim()) return result.name;
          if (result.id) return result.id.split('/').pop().replace('.pdf', '');
          return "Unnamed Candidate";
        };

        const removalData = {
          candidateId: candidate.id,
          candidateName: getDisplayName(candidate),
          matchScore: candidate.similarity,
          keywordMatches: candidate.keyword_match_count || 0,
          combinedScore: candidate.combined_score || 0,
          removalReason: reason,
          timestamp: new Date().toISOString(),
          jobTitle: candidate.title,
          skills: candidate.matchingContext?.key_matches || []
        };

        await handleRemoveCandidate(candidateId, removalData);
      }
    }

    setSelectedCandidates(new Set());
    setSelectAll(false);
  };

  // Calculate metrics
  const metrics = useMemo(() => {
    const total = results.length;
    const highRated = results.filter(r => (r.combined_score || r.score || 0) >= 0.9).length;
    const contacted = results.filter(r => r.contactHistory?.contacted).length;
    const perfectMatches = results.filter(r => (r.combined_score || r.score || 0) >= 0.95).length;

    return {
      candidatesFound: total,
      highRated,
      contacted,
      perfectMatches
    };
  }, [results]);

  if (!results || results.length === 0) {
    return (
      <div className="flex justify-center items-center h-40 text-lg text-gray-500">
        No results found
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Metric Cards */}
      <MetricCards
        candidatesFound={metrics.candidatesFound}
        highRated={metrics.highRated}
        contacted={metrics.contacted}
        perfectMatches={metrics.perfectMatches}
      />

      {/* Results Header with Bulk Actions */}
      <ResultsHeader
        jobTitle="Initial Search Results"
        totalCount={initialResults?.length || 0}
        showingCount={results.length}
        selectAll={selectAll}
        onSelectAllChange={handleSelectAll}
        selectedCount={selectedCandidates.size}
        onBulkRemove={handleBulkRemove}
      />

      {/* Candidate Cards */}
      <div className="bg-white rounded-b-lg shadow-sm border border-t-0 border-gray-200">
        <div className="p-6 space-y-4 max-h-[600px] overflow-y-auto custom-scrollbar">
          {results.map((result) => (
            <CandidateCard
              key={result.id}
              candidate={result}
              selected={selectedCandidates.has(result.id)}
              onSelect={() => handleSelectCandidate(result.id)}
              onRemove={handleShowRemoveModal}
              isEvaluationResult={false}
              expanded={expandedResults.has(result.id)}
              onToggleExpand={() => toggleExpand(result.id)}
              expandedContent={
                <>
                  <CVExpandedSection
                    candidate={result}
                    onCollapse={() => toggleExpand(result.id)}
                    showQuickActions={true}
                  />
                  {result.contactHistory?.contacted && (
                    <div className="mt-4">
                      <ContactHistorySection contactHistory={result.contactHistory} />
                    </div>
                  )}
                </>
              }
            />
          ))}
        </div>
      </div>

      {/* Remove Reason Modal */}
      <RemoveReasonModal
        isOpen={showRemoveModal}
        onClose={() => {
          setShowRemoveModal(false);
          setCandidateToRemove(null);
        }}
        onConfirm={handleConfirmRemoval}
        candidate={candidateToRemove}
      />
    </div>
  );
}

export default ModernQueryResultsList;























